// ═══════════════════════════════════════════════════════════════
// VOLUNTEEROS — Schedule
// All assignments in a schedule view with Upcoming/Past tabs,
// grouping by shiftDate, with filters and status actions
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Select,
  Loader, Center, Table, ActionIcon, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCalendar, IconCheck, IconX, IconBan,
  IconFilter,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import type { VolAssignment } from '../../store/volunteerStore';

// ── Status badge config ──────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'blue',
  confirmed: 'green',
  completed: 'teal',
  'no-show': 'red',
  cancelled: 'gray',
  declined: 'orange',
};

export default function VolunteerSchedule() {
  const {
    volunteers, events, assignments,
    loadVolunteers, loadEvents, loadAssignments, updateAssignment,
  } = useVolunteerStore();
  const { activeIncident } = useAuthStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [filterVolunteer, setFilterVolunteer] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [filterEvent, setFilterEvent] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    await Promise.all([loadVolunteers(), loadEvents(), loadAssignments()]);
  }, [loadVolunteers, loadEvents, loadAssignments]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // ── Lookups ────────────────────────────────────────────────

  const volunteerName = useCallback((volId: string) => {
    const v = volunteers.find(vol => String(vol.id) === String(volId));
    return v ? `${v.firstName} ${v.lastName}`.trim() || 'Unknown' : 'Unknown';
  }, [volunteers]);

  const eventName = useCallback((evId: string) => {
    const e = events.find(ev => String(ev.id) === String(evId));
    return e?.name || 'Unknown';
  }, [events]);

  // ── Filter options ─────────────────────────────────────────

  const volunteerOptions = useMemo(() =>
    volunteers.map(v => ({
      value: String(v.id),
      label: `${v.firstName} ${v.lastName}`.trim() || `#${v.id}`,
    })),
    [volunteers],
  );

  const eventOptions = useMemo(() =>
    events.map(e => ({
      value: String(e.id),
      label: e.name || `#${e.id}`,
    })),
    [events],
  );

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'no-show', label: 'No-Show' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'declined', label: 'Declined' },
  ];

  // ── Filtered + split assignments ───────────────────────────

  const now = dayjs();

  const filtered = useMemo(() => {
    let list = [...assignments];
    if (filterVolunteer) list = list.filter(a => String(a.volunteerId) === filterVolunteer);
    if (filterEvent) list = list.filter(a => String(a.eventId) === filterEvent);
    if (filterStatus) list = list.filter(a => a.status === filterStatus);
    if (activeIncident) list = list.filter(a => String(a.incidentId) === String(activeIncident.id));
    return list;
  }, [assignments, filterVolunteer, filterEvent, filterStatus, activeIncident]);

  const { upcoming, past } = useMemo(() => {
    const up: VolAssignment[] = [];
    const pa: VolAssignment[] = [];
    filtered.forEach(a => {
      const d = a.shiftDate ? dayjs(a.shiftDate) : null;
      if (d && d.isBefore(now, 'day')) {
        pa.push(a);
      } else {
        up.push(a);
      }
    });
    up.sort((a, b) => (a.shiftDate ?? '').localeCompare(b.shiftDate ?? ''));
    pa.sort((a, b) => (b.shiftDate ?? '').localeCompare(a.shiftDate ?? ''));
    return { upcoming: up, past: pa };
  }, [filtered, now]);

  // Group assignments by shiftDate
  const groupByDate = (list: VolAssignment[]): Array<{ date: string; items: VolAssignment[] }> => {
    const map = new Map<string, VolAssignment[]>();
    for (const a of list) {
      const key = a.shiftDate || 'No Date';
      const arr = map.get(key);
      if (arr) arr.push(a);
      else map.set(key, [a]);
    }
    return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
  };

  // ── Status actions ─────────────────────────────────────────

  const handleStatusChange = async (id: number, newStatus: string) => {
    setUpdatingId(id);
    try {
      await updateAssignment(id, { status: newStatus });
      notifications.show({
        title: 'Assignment updated',
        message: `Status changed to ${newStatus}`,
        color: STATUS_COLORS[newStatus] ?? 'blue',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update assignment status',
        color: 'red',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // Determine which action buttons to show based on current status
  const renderActions = (a: VolAssignment) => {
    const loading = updatingId === a.id;
    const btns: React.ReactNode[] = [];

    if (a.status === 'scheduled') {
      btns.push(
        <Tooltip label="Confirm" key="confirm">
          <ActionIcon size="sm" variant="light" color="green" loading={loading}
            onClick={() => handleStatusChange(a.id, 'confirmed')}>
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>,
        <Tooltip label="Decline" key="decline">
          <ActionIcon size="sm" variant="light" color="orange" loading={loading}
            onClick={() => handleStatusChange(a.id, 'declined')}>
            <IconX size={14} />
          </ActionIcon>
        </Tooltip>,
        <Tooltip label="Cancel" key="cancel">
          <ActionIcon size="sm" variant="light" color="gray" loading={loading}
            onClick={() => handleStatusChange(a.id, 'cancelled')}>
            <IconBan size={14} />
          </ActionIcon>
        </Tooltip>,
      );
    } else if (a.status === 'confirmed') {
      btns.push(
        <Tooltip label="Mark Completed" key="complete">
          <ActionIcon size="sm" variant="light" color="teal" loading={loading}
            onClick={() => handleStatusChange(a.id, 'completed')}>
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>,
        <Tooltip label="Cancel" key="cancel">
          <ActionIcon size="sm" variant="light" color="gray" loading={loading}
            onClick={() => handleStatusChange(a.id, 'cancelled')}>
            <IconBan size={14} />
          </ActionIcon>
        </Tooltip>,
      );
    }

    return btns.length > 0 ? <Group gap={4}>{btns}</Group> : <Text size="xs" c="dimmed">—</Text>;
  };

  // ── Render a date-grouped section ──────────────────────────

  const renderGroup = (list: VolAssignment[]) => {
    const groups = groupByDate(list);
    if (groups.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No assignments to display.
        </Text>
      );
    }

    return (
      <Stack gap="md">
        {groups.map(g => (
          <Paper key={g.date} p="md" radius="sm" withBorder>
            <Group gap="xs" mb="sm">
              <IconCalendar size={14} />
              <Text fw={600} size="sm">
                {g.date !== 'No Date' ? dayjs(g.date).format('ddd, MMM D YYYY') : 'No Date'}
              </Text>
              <Badge size="xs" variant="light" color="gray">{g.items.length}</Badge>
            </Group>
            <Table.ScrollContainer minWidth={500}>
              <Table verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Volunteer</Table.Th>
                    <Table.Th>Event</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Time</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {g.items.map(a => (
                    <Table.Tr key={a.id}>
                      <Table.Td>{volunteerName(a.volunteerId)}</Table.Td>
                      <Table.Td>{eventName(a.eventId)}</Table.Td>
                      <Table.Td>{a.role || '—'}</Table.Td>
                      <Table.Td>
                        {a.startTime || a.endTime
                          ? `${a.startTime || '?'} - ${a.endTime || '?'}`
                          : '—'}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={STATUS_COLORS[a.status] ?? 'gray'} tt="capitalize">
                          {a.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{renderActions(a)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        ))}
      </Stack>
    );
  };

  // ── Main render ────────────────────────────────────────────

  if (initialLoad) {
    return <Center h={400}><Loader color="blue" /></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Schedule</Title>
          <Text size="sm" c="dimmed">
            {filtered.length} assignment{filtered.length !== 1 ? 's' : ''}{' '}
            {activeIncident ? `for ${activeIncident.name}` : 'across all incidents'}
          </Text>
        </div>
        <Group gap="xs" align="flex-end">
          <IconFilter size={14} style={{ color: 'var(--slate)', marginBottom: 8 }} />
          <Select
            placeholder="Volunteer"
            data={volunteerOptions}
            value={filterVolunteer}
            onChange={setFilterVolunteer}
            clearable
            searchable
            size="xs"
            w={160}
          />
          <Select
            placeholder="Event"
            data={eventOptions}
            value={filterEvent}
            onChange={setFilterEvent}
            clearable
            searchable
            size="xs"
            w={160}
          />
          <Select
            placeholder="Status"
            data={statusOptions}
            value={filterStatus}
            onChange={setFilterStatus}
            clearable
            size="xs"
            w={130}
          />
        </Group>
      </Group>

      <Group gap="xs" mb="sm">
        <Select
          placeholder="Time"
          data={[
            { value: 'all', label: `All (${filtered.length})` },
            { value: 'upcoming', label: `Upcoming (${upcoming.length})` },
            { value: 'past', label: `Past (${past.length})` },
          ]}
          value={timeFilter}
          onChange={v => setTimeFilter(v ?? 'all')}
          size="xs"
          w={160}
        />
      </Group>

      {renderGroup(timeFilter === 'past' ? past : timeFilter === 'upcoming' ? upcoming : [...upcoming, ...past])}
    </div>
  );
}
